/**
 * Task Store
 * 
 * Zustand store for managing task state across the application.
 * Handles real-time task updates from WebSocket and provides
 * methods for task CRUD operations.
 * 
 * IMPORTANT: Tasks are scoped per-project and per-session.
 * When switching projects, tasks are cleared and reloaded from backend.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getAuthToken } from '../utils/auth';
import { refreshAuthToken } from '../services/cloudApi';

const DEPLOYMENT_MODE = process.env.REACT_APP_DEPLOYMENT_MODE || 'local';
const CLOUD_API_URL = process.env.REACT_APP_CLOUD_API_URL || '';
const LOCAL_API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function getTaskApiBase() {
    if (DEPLOYMENT_MODE === 'cloud') {
        return `${CLOUD_API_URL}/api/proxy`;
    }
    return LOCAL_API_BASE;
}

function getAuthHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    if (DEPLOYMENT_MODE === 'cloud') {
        const token = getAuthToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }
    return headers;
}

async function taskFetch(url, options = {}, _isRetry = false) {
    const response = await fetch(url, {
        ...options,
        headers: { ...getAuthHeaders(), ...options.headers },
    });

    if (response.status === 401 && DEPLOYMENT_MODE === 'cloud' && !_isRetry) {
        const refreshed = await refreshAuthToken();
        if (refreshed) {
            return taskFetch(url, options, true);
        }
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }

    return response;
}

/**
 * Sort tasks by order field, with fallback to extracting phase number from content
 */
function sortTasksByOrder(tasks) {
    return [...tasks].sort((a, b) => {
        // Primary sort: by order field
        if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
        }
        
        // Secondary sort: try to extract phase number from content (e.g., "Phase 0:", "Phase 1:")
        const phaseMatch = (content) => {
            const match = content?.match(/Phase\s*(\d+)/i);
            return match ? parseInt(match[1], 10) : 999;
        };
        
        const phaseA = phaseMatch(a.content);
        const phaseB = phaseMatch(b.content);
        
        if (phaseA !== phaseB) {
            return phaseA - phaseB;
        }
        
        // Fallback: by created_at timestamp
        return (a.created_at || '').localeCompare(b.created_at || '');
    });
}

/**
 * Calculate stats from tasks array
 */
function calculateStats(tasks) {
    const total = tasks.length;
    const pending = tasks.filter(t => t.status === 'pending').length;
    const in_progress = tasks.filter(t => t.status === 'in_progress').length;
    const completed = tasks.filter(t => t.status === 'completed').length;
    const cancelled = tasks.filter(t => t.status === 'cancelled').length;

    const activeTotal = total - cancelled;
    const progress_percent = activeTotal > 0
        ? Math.round((completed / activeTotal) * 100 * 10) / 10
        : 0;

    return {
        total,
        pending,
        in_progress,
        completed,
        cancelled,
        progress_percent
    };
}

const emptyStats = {
    total: 0,
    pending: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0,
    progress_percent: 0
};

export const useTaskStore = create(
    persist(
        (set, get) => ({
            // State - tasks are stored per-project
            tasks: [],
            stats: { ...emptyStats },
            isLoading: false,
            error: null,
            sessionId: null,
            projectId: null,
            isExpanded: true, // UI state for panel expansion
            activeAgent: null, // Currently running agent name

            /**
             * Load tasks from backend for current project/session
             */
            loadTasks: async (projectId, sessionId) => {
                if (!projectId || !sessionId) {
                    console.warn('[TaskStore] loadTasks called without projectId or sessionId');
                    return;
                }

                const currentProjectId = get().projectId;
                
                // If different project, clear first
                if (projectId !== currentProjectId) {
                    set({
                        tasks: [],
                        stats: { ...emptyStats },
                        projectId: projectId,
                        sessionId: sessionId,
                        error: null
                    });
                }

                set({ isLoading: true, projectId, sessionId });

                try {
                    const response = await taskFetch(
                        `${getTaskApiBase()}/api/projects/${projectId}/sessions/${sessionId}/tasks`
                    );

                    if (!response.ok) {
                        // 404 is okay - just means no tasks yet
                        if (response.status === 404) {
                            set({
                                tasks: [],
                                stats: { ...emptyStats },
                                isLoading: false,
                                error: null
                            });
                            return;
                        }
                        throw new Error(`Failed to load tasks: ${response.statusText}`);
                    }

                    const data = await response.json();
                    const sortedTasks = sortTasksByOrder(data.tasks || []);
                    set({
                        tasks: sortedTasks,
                        stats: data.stats || calculateStats(sortedTasks),
                        isLoading: false,
                        error: null
                    });
                } catch (error) {
                    console.error('[TaskStore] Error loading tasks:', error);
                    set({ error: error.message, isLoading: false });
                }
            },

            /**
             * Handle WebSocket task events (real-time updates from agent)
             */
            handleTaskEvent: (event) => {
                const { event_type, data } = event;
                const { projectId, sessionId, tasks } = get();

                console.log('[TaskStore] ==================');
                console.log('[TaskStore] Task event received:', event_type);
                console.log('[TaskStore] Event data:', data);
                console.log('[TaskStore] Current state:', { projectId, sessionId, taskCount: tasks.length });
                console.log('[TaskStore] ==================');

                switch (event_type) {
                    case 'task_list_created':
                        // New task list created - refresh all
                        if (projectId && sessionId) {
                            get().loadTasks(projectId, sessionId);
                        }
                        break;

                    case 'task_added':
                        if (data?.task) {
                            set(state => {
                                // Check for duplicates before adding
                                const exists = state.tasks.some(t => t.id === data.task.id);
                                if (exists) {
                                    console.log('[TaskStore] Task already exists, updating instead:', data.task.id);
                                    const updatedTasks = state.tasks.map(t =>
                                        t.id === data.task.id ? data.task : t
                                    );
                                    const newTasks = sortTasksByOrder(updatedTasks);
                                    return {
                                        tasks: newTasks,
                                        stats: calculateStats(newTasks)
                                    };
                                }
                                
                                console.log('[TaskStore] Adding task:', data.task.id, data.task.content);
                                const newTasks = sortTasksByOrder([...state.tasks, data.task]);
                                return {
                                    tasks: newTasks,
                                    stats: calculateStats(newTasks)
                                };
                            });
                        } else {
                            console.warn('[TaskStore] task_added event missing task data');
                        }
                        break;

                    case 'task_updated':
                        if (data?.task) {
                            set(state => {
                                console.log('[TaskStore] Updating task:', data.task.id, 'status:', data.task.status);
                                const updatedTasks = state.tasks.map(t =>
                                    t.id === data.task.id ? data.task : t
                                );
                                // Re-sort in case order changed
                                const newTasks = sortTasksByOrder(updatedTasks);
                                return {
                                    tasks: newTasks,
                                    stats: calculateStats(newTasks)
                                };
                            });
                        } else {
                            console.warn('[TaskStore] task_updated event missing task data');
                        }
                        break;

                    case 'task_deleted':
                        set(state => {
                            const taskId = data?.task_id || data?.task?.id;
                            if (taskId) {
                                console.log('[TaskStore] Deleting task:', taskId);
                                const newTasks = state.tasks.filter(t => t.id !== taskId);
                                return {
                                    tasks: newTasks,
                                    stats: calculateStats(newTasks)
                                };
                            }
                            return state;
                        });
                        break;

                    default:
                        console.log('[TaskStore] Unknown task event type:', event_type);
                }
            },

            /**
             * Add task from UI
             */
            addTask: async (content, parentId = null) => {
                const { projectId, sessionId } = get();
                if (!projectId || !sessionId) {
                    console.error('[TaskStore] No project or session ID set');
                    return null;
                }

                try {
                    const response = await taskFetch(
                        `${getTaskApiBase()}/api/projects/${projectId}/sessions/${sessionId}/tasks`,
                        {
                            method: 'POST',
                            body: JSON.stringify({ content, parent_id: parentId })
                        }
                    );

                    if (!response.ok) {
                        throw new Error(`Failed to create task: ${response.statusText}`);
                    }

                    const data = await response.json();

                    // Add to local state (sorted)
                    set(state => {
                        const newTasks = sortTasksByOrder([...state.tasks, data.task]);
                        return {
                            tasks: newTasks,
                            stats: calculateStats(newTasks)
                        };
                    });

                    return data.task;
                } catch (error) {
                    console.error('[TaskStore] Error creating task:', error);
                    set({ error: error.message });
                    return null;
                }
            },

            /**
             * Update task from UI
             */
            updateTask: async (taskId, updates) => {
                const { projectId, sessionId } = get();
                if (!projectId || !sessionId) return null;

                try {
                    const response = await taskFetch(
                        `${getTaskApiBase()}/api/projects/${projectId}/sessions/${sessionId}/tasks/${taskId}`,
                        {
                            method: 'PATCH',
                            body: JSON.stringify(updates)
                        }
                    );

                    if (!response.ok) {
                        throw new Error(`Failed to update task: ${response.statusText}`);
                    }

                    const data = await response.json();

                    // Update local state
                    set(state => {
                        const newTasks = state.tasks.map(t =>
                            t.id === taskId ? data.task : t
                        );
                        return {
                            tasks: newTasks,
                            stats: calculateStats(newTasks)
                        };
                    });

                    return data.task;
                } catch (error) {
                    console.error('[TaskStore] Error updating task:', error);
                    set({ error: error.message });
                    return null;
                }
            },

            /**
             * Delete task from UI
             */
            deleteTask: async (taskId) => {
                const { projectId, sessionId } = get();
                if (!projectId || !sessionId) return false;

                try {
                    const response = await taskFetch(
                        `${getTaskApiBase()}/api/projects/${projectId}/sessions/${sessionId}/tasks/${taskId}`,
                        { method: 'DELETE' }
                    );

                    if (!response.ok) {
                        throw new Error(`Failed to delete task: ${response.statusText}`);
                    }

                    // Remove from local state (including subtasks)
                    set(state => {
                        const newTasks = state.tasks.filter(t => 
                            t.id !== taskId && t.parent_id !== taskId
                        );
                        return {
                            tasks: newTasks,
                            stats: calculateStats(newTasks)
                        };
                    });

                    return true;
                } catch (error) {
                    console.error('[TaskStore] Error deleting task:', error);
                    set({ error: error.message });
                    return false;
                }
            },

            /**
             * Toggle task status (pending -> in_progress -> completed)
             */
            toggleTaskStatus: async (taskId) => {
                const task = get().tasks.find(t => t.id === taskId);
                if (!task) return null;

                const statusCycle = {
                    'pending': 'in_progress',
                    'in_progress': 'completed',
                    'completed': 'pending',
                    'cancelled': 'pending'
                };

                const newStatus = statusCycle[task.status] || 'pending';
                return get().updateTask(taskId, { status: newStatus });
            },

            /**
             * Set panel expansion state
             */
            setExpanded: (expanded) => {
                set({ isExpanded: expanded });
            },

            /**
             * Toggle panel expansion
             */
            toggleExpanded: () => {
                set(state => ({ isExpanded: !state.isExpanded }));
            },

            /**
             * Set the currently active agent
             */
            setActiveAgent: (agentName) => {
                set({ activeAgent: agentName });
            },

            /**
             * Clear tasks (for project switch or cleanup)
             */
            clearTasks: () => {
                set({
                    tasks: [],
                    stats: { ...emptyStats },
                    error: null,
                    activeAgent: null
                });
            },

            /**
             * Reset entire store (full cleanup)
             */
            reset: () => {
                set({
                    tasks: [],
                    stats: { ...emptyStats },
                    isLoading: false,
                    error: null,
                    sessionId: null,
                    projectId: null,
                    isExpanded: true,
                    activeAgent: null
                });
            }
        }),
        {
            name: 'task-storage',
            // Only persist UI state (isExpanded), NOT task data
            // Task data should always come from backend per-project
            partialize: (state) => ({
                isExpanded: state.isExpanded
            }),
            // On rehydration, don't restore tasks - they'll be loaded fresh
            onRehydrateStorage: () => (state) => {
                if (state) {
                    // Clear any stale task data on app load
                    state.tasks = [];
                    state.stats = { ...emptyStats };
                    state.sessionId = null;
                    state.projectId = null;
                }
            }
        }
    )
);

export default useTaskStore;
